import m, {Vnode} from "mithril";
import BaseComponent from "../../BaseComponent";
import {ComponentContext} from "../../IComponent";
import GoalsSidebarView from "./view/GoalsSidebarView";
import ThreadMainView, {TThreadMainCallbacks} from "./view/ThreadMainView";
import InterviewThreadApi from "./model/InterviewThreadApi";
import {TInterviewMessage} from "./model/TInterviewThread";
import {
    TCompletionModalOptions,
    TInterviewThreadCompletionModalViewModel,
    TInterviewThreadGoalsSidebarViewModel,
    TInterviewThreadMainViewModel,
} from "./model/TInterviewThreadViewModel";
import ProcessingJobMonitor from "./model/ProcessingJobMonitor";
import VoiceInputController from "./model/VoiceInputController";
import VoicePlaybackController from "./model/VoicePlaybackController";
import InterviewThreadStore from "./model/InterviewThreadStore";
import InterviewThreadController from "./model/InterviewThreadController";
import InterviewThreadViewportController from "./model/InterviewThreadViewportController";
import ProcessingMessageController from "./model/ProcessingMessageController";
import {formatInterviewElapsedTime, getInterviewElapsedSeconds} from "./model/InterviewTimer";

export default class InterviewThread extends BaseComponent {

    private static instanceCounter = 0;
    private static readonly PROCESSING_MESSAGE_KEYS = [
        "interview_thread_tina_thinking",
        "interview_thread_tina_writing_message",
    ];
    private static readonly GOALS_SIDEBAR_DEFAULT_OPEN_MEDIA_QUERY = "(min-width: 1400px)";

    private readonly store: InterviewThreadStore;
    private readonly controller: InterviewThreadController;
    private readonly viewportController: InterviewThreadViewportController;
    private readonly processingMessageController: ProcessingMessageController;
    private readonly voiceInputController: VoiceInputController;
    private readonly voicePlaybackController: VoicePlaybackController;
    private readonly avatarPath: string;
    private readonly nick: string;
    private readonly showAnonymized: boolean;
    private readonly showConversationActions: boolean;
    private readonly restartConversationUrl: string|null;
    private readonly contactModeratorUrl: string|null;
    private readonly testMode: boolean;
    private readonly backToEditUrl: string|null;
    private readonly voicePlaybackDisabled: boolean;
    private readonly completionModalOptions: TCompletionModalOptions|null;
    private readonly autoPlaybackId: string;
    private readonly completionModalTitleId: string;
    private readonly completionModalBodyId: string;
    private readonly threadMainView = new ThreadMainView();
    private readonly goalsSidebarView = new GoalsSidebarView();
    private readonly threadMainCallbacks: TThreadMainCallbacks;
    private voicePlaybackError: string|null = null;
    private collapseInputWhenProcessingBubbleRendered = false;
    private timerIntervalId: number|null = null;
    private goalsSidebarOpen = false;
    private elapsedTime = "00:00";

    constructor(
        $component: JQuery<HTMLElement>,
        context?: ComponentContext,
    ) {
        super($component, context);

        const instanceId = ++InterviewThread.instanceCounter;
        const threadId = Number(this.options?.threadId ?? 0);
        const interviewId = Number(this.options?.interviewId ?? 0);
        const readonly = Boolean(this.options?.readonly);
        this.avatarPath = this.options?.avatar ?? "";
        this.nick = this.options?.nick ?? this.translator.translate("you");
        this.showAnonymized = Boolean(this.options?.showAnonymized);
        this.showConversationActions = Boolean(this.options?.showConversationActions);
        this.restartConversationUrl = typeof this.options?.restartConversationUrl === "string"
            ? this.options.restartConversationUrl
            : null;
        this.contactModeratorUrl = typeof this.options?.contactModeratorUrl === "string"
            ? this.options.contactModeratorUrl
            : null;
        this.testMode = Boolean(this.options?.testMode);
        this.backToEditUrl = this.options?.backToEditUrl ?? null;
        const initiallyCompleted = Boolean(this.options?.initiallyCompleted);
        this.voicePlaybackDisabled = initiallyCompleted && !readonly && !this.testMode;
        this.completionModalOptions = initiallyCompleted ? null : this.options?.completionModal ?? null;
        this.autoPlaybackId = `interview-thread-auto-playback-${instanceId}`;
        this.completionModalTitleId = `interview-thread-completion-title-${instanceId}`;
        this.completionModalBodyId = `interview-thread-completion-body-${instanceId}`;

        const api = new InterviewThreadApi({
            thread: this.options?.urlGetThread,
            sendMessage: this.options?.urlSendMessage,
            transcribeVoiceMessage: this.options?.urlTranscribeVoiceMessage,
            synthesizeVoiceMessage: this.options?.urlSynthesizeVoiceMessage,
            processingJobStatus: this.options?.urlGetProcessingJobStatus,
            requeueProcessingJob: this.options?.urlRequeueProcessingJob,
        });
        this.store = new InterviewThreadStore(readonly);
        this.viewportController = new InterviewThreadViewportController(
            this.$component[0],
            () => this.getMainViewModel().inputDisabled,
        );
        this.processingMessageController = new ProcessingMessageController(
            InterviewThread.PROCESSING_MESSAGE_KEYS,
            key => this.translator.translate(key),
            () => m.redraw(),
        );
        this.voicePlaybackController = new VoicePlaybackController(
            api,
            threadId,
            Boolean(this.options?.voicePlaybackEnabled),
            () => m.redraw(),
            () => {
                this.voicePlaybackError = this.translator.translate("interview_thread_playback_failed");
            },
        );
        this.voiceInputController = new VoiceInputController(
            api,
            threadId,
            Boolean(this.options?.voiceInputEnabled),
            key => this.translator.translate(key),
            text => this.applyVoiceTranscription(text),
            () => this.voicePlaybackController.stop(),
            () => m.redraw(),
        );
        const processingJobMonitor = new ProcessingJobMonitor(
            (jobId, signal) => api.getProcessingJobStatus(jobId, signal),
        );
        this.controller = new InterviewThreadController(
            api,
            this.store,
            processingJobMonitor,
            {
                onChange: () => this.onConversationChange(),
                onMessagesChanged: () => this.viewportController.requestScrollToBottom(),
                onWaitingForUser: () => this.viewportController.requestFocus(false),
                onNewAgentMessages: (messages, previousMessageIds) => {
                    for (let index = messages.length - 1; index >= 0; index--) {
                        const message = messages[index];
                        if (message.role === "agent" && !previousMessageIds.has(message.id)) {
                            this.viewportController.requestScrollToAgentMessage(message.id);
                            break;
                        }
                    }
                    this.voicePlaybackController.playLatestNewAgentMessage(messages, previousMessageIds);
                },
            },
            key => this.translator.translate(key),
            interviewId,
            this.testMode,
        );
        this.threadMainCallbacks = {
            bindMessagesContainer: element => this.viewportController.bindMessagesContainer(element),
            focusInputIfRequested: element => this.viewportController.focusInputIfRequested(element),
            onInputFocus: () => this.viewportController.expandInput(),
            onInputBlur: () => {
                if (!this.getMainViewModel().inputDisabled) {
                    this.viewportController.collapseInput();
                }
            },
            onInputTransitionEnd: event => {
                if (event.propertyName === "height") {
                    this.viewportController.requestScrollToBottom();
                }
            },
            onInput: value => this.onInput(value),
            onInputKeydown: event => this.onInputKeydown(event),
            onSend: () => this.onSend(),
            onSuggestedResponse: response => this.onSuggestedResponse(response),
            onRetry: () => this.controller.retry(),
            onProcessingBubbleRendered: () => this.onProcessingBubbleRendered(),
            onToggleVoicePlayback: message => this.onToggleVoicePlayback(message),
            onToggleVoiceAutoPlayback: () => this.onToggleVoiceAutoPlayback(),
            onToggleVoiceRecording: () => this.onToggleVoiceRecording(),
            onCompletionModalAction: () => this.onCompletionModalAction(),
        };
    }

    public async init(): Promise<void> {
        this.goalsSidebarOpen = this.getDefaultGoalsSidebarOpen();
        this.viewportController.start();
        m.mount(this.$component[0], this);
        await this.controller.init();
    }

    public beforeRemove(): void {
        this.stopTimer();
        this.collapseInputWhenProcessingBubbleRendered = false;
        this.controller.destroy();
        this.processingMessageController.destroy();
        this.voicePlaybackController.destroy();
        this.voiceInputController.destroy();
        this.viewportController.destroy();
        m.mount(this.$component[0], null);
    }

    private onConversationChange(): void {
        const processing = this.store.getState().conversation.type === "processing";
        if (!processing) {
            this.collapseInputWhenProcessingBubbleRendered = false;
        }
        this.syncTimer();
        this.processingMessageController.sync(processing);
        m.redraw();
    }

    private onInput(value: string): void {
        this.voiceInputController.clearError();
        this.voicePlaybackError = null;
        this.controller.onInput(value);
    }

    private async onInputKeydown(event: KeyboardEvent): Promise<void> {
        if (event.key === "Enter" && !event.shiftKey && this.getMainViewModel().canSend) {
            event.preventDefault();
            await this.onSend();
        }
    }

    private async onSend(): Promise<void> {
        if (!this.getMainViewModel().canSend) {
            return;
        }
        await this.controller.send();
        if (this.store.getState().conversation.type === "processing") {
            this.collapseInputWhenProcessingBubbleRendered = true;
            m.redraw();
        }
    }

    private async onSuggestedResponse(response: string): Promise<void> {
        if (!this.store.canShowSuggestedResponses()) {
            return;
        }

        this.onInput(response);
        await this.onSend();
    }

    private onProcessingBubbleRendered(): void {
        if (!this.collapseInputWhenProcessingBubbleRendered
            || this.store.getState().conversation.type !== "processing"
        ) {
            return;
        }

        this.collapseInputWhenProcessingBubbleRendered = false;
        this.viewportController.collapseInput();
        this.viewportController.requestScrollToBottom();
        m.redraw();
    }

    private async onToggleVoicePlayback(message: TInterviewMessage): Promise<void> {
        if (this.voicePlaybackDisabled) {
            return;
        }
        this.voicePlaybackError = null;
        await this.voicePlaybackController.toggle(message);
    }

    private async onToggleVoiceAutoPlayback(): Promise<void> {
        if (this.voicePlaybackDisabled) {
            return;
        }
        this.voicePlaybackError = null;
        await this.voicePlaybackController.toggleAutoPlayback(this.store.getState().messages);
    }

    private async onToggleVoiceRecording(): Promise<void> {
        this.viewportController.collapseInput();
        await this.voiceInputController.toggle(this.canStartVoiceRecording());
    }

    private canStartVoiceRecording(): boolean {
        const voiceState = this.voiceInputController.getState();

        return this.voiceInputController.isSupported()
            && !this.store.isInputDisabled()
            && (voiceState === "idle" || voiceState === "failed");
    }

    private applyVoiceTranscription(text: string): void {
        const input = this.store.getState().input.trim();
        this.controller.onInput(input ? `${input} ${text}` : text);
        this.viewportController.expandInput();
        this.viewportController.requestScrollToBottom();
        this.viewportController.requestFocus(true);
    }

    private onCompletionModalAction(): void {
        const redirectUrl = this.completionModalOptions?.redirectUrl;
        if (redirectUrl) {
            window.location.href = redirectUrl;
        }
    }

    private getMainViewModel(): TInterviewThreadMainViewModel {
        const state = this.store.getState();
        const conversation = state.conversation;
        const voiceState = this.voiceInputController.getState();
        const voiceInputBusy = voiceState === "requestingPermission"
            || voiceState === "recording"
            || voiceState === "transcribing";
        const inputTooLong = this.store.isInputTooLong();
        const error = this.store.getError();
        const conversationError = inputTooLong
            ? this.translator.translate("interview_thread_message_too_long").replace("%max%", "10000")
            : error?.message ?? null;
        const canRetryConversationError = !inputTooLong
            && error?.retryable === true
            && (conversation.type === "loadFailed"
                || conversation.type === "sendFailed"
                || conversation.type === "processingFailed"
                || conversation.type === "reconciliationRequired");

        return {
            avatarPath: this.avatarPath,
            nick: this.nick,
            showAnonymized: this.showAnonymized,
            goalsSidebarOpen: this.goalsSidebarOpen,
            agentName: this.translator.translate("interview_thread_agent_name"),
            autoPlaybackId: this.autoPlaybackId,
            loading: conversation.type === "loading",
            messages: state.messages,
            input: state.input,
            inputExpanded: this.viewportController.isExpanded(),
            inputTooLong,
            showProcessingBubble: conversation.type === "processing",
            processingMessage: this.processingMessageController.getMessage(),
            conversationError,
            canRetryConversationError,
            threadStatusMessage: conversation.type === "closed"
                ? this.translator.translate(
                    conversation.status === "cancelled"
                        ? "interview_thread_cancelled"
                        : "interview_thread_conversation_failed",
                )
                : null,
            canSend: this.store.canSend() && !voiceInputBusy,
            showSuggestedResponses: this.store.canShowSuggestedResponses(),
            inputDisabled: this.store.isInputDisabled() || voiceInputBusy,
            showConversationActions: this.showConversationActions,
            restartConversationUrl: this.restartConversationUrl,
            contactModeratorUrl: this.contactModeratorUrl,
            ttsSupported: this.voicePlaybackController.isSupported()
                && state.messages.some(message => message.role === "agent" && message.speechAvailable),
            voicePlaybackDisabled: this.voicePlaybackDisabled,
            playingMessageId: this.voicePlaybackController.getPlayingMessageId(),
            voiceAutoPlaybackEnabled: this.voicePlaybackController.isAutoPlaybackEnabled(),
            voiceRecordingSupported: this.voiceInputController.isSupported(),
            requestingVoicePermission: voiceState === "requestingPermission",
            recordingVoice: voiceState === "recording",
            voiceRecordingLevel: this.voiceInputController.getRecordingLevel(),
            transcribingVoice: voiceState === "transcribing",
            canToggleVoiceRecording: voiceState === "recording" || this.canStartVoiceRecording(),
            voiceError: this.voiceInputController.getError() ?? this.voicePlaybackError,
            completionModal: this.getCompletionModalViewModel(),
        };
    }

    private getDefaultGoalsSidebarOpen(): boolean {
        return this.testMode
            && window.matchMedia(InterviewThread.GOALS_SIDEBAR_DEFAULT_OPEN_MEDIA_QUERY).matches;
    }

    private syncTimer(): void {
        this.stopTimer();
        this.updateElapsedTime();

        const state = this.store.getState();
        if (!this.testMode || !state.startedAt || state.completedAt || state.closedAt) {
            return;
        }

        this.timerIntervalId = window.setInterval(() => {
            this.updateElapsedTime();
            m.redraw();
        }, 1000);
    }

    private updateElapsedTime(): void {
        const state = this.store.getState();
        if (!state.startedAt) {
            this.elapsedTime = "00:00";
            return;
        }

        this.elapsedTime = formatInterviewElapsedTime(
            getInterviewElapsedSeconds(state.startedAt, state.completedAt ?? state.closedAt),
        );
    }

    private stopTimer(): void {
        if (this.timerIntervalId !== null) {
            window.clearInterval(this.timerIntervalId);
            this.timerIntervalId = null;
        }
    }

    private getGoalsSidebarViewModel(): TInterviewThreadGoalsSidebarViewModel {
        const state = this.store.getState();

        return {
            testMode: this.testMode,
            open: this.goalsSidebarOpen,
            goals: state.goals,
            backToEditUrl: this.backToEditUrl,
            elapsedTime: this.elapsedTime,
            completed: state.conversation.type === "completed",
        };
    }

    private getCompletionModalViewModel(): TInterviewThreadCompletionModalViewModel|null {
        const redirectUrl = this.completionModalOptions?.redirectUrl;
        if (this.store.getState().conversation.type !== "completed" || !redirectUrl) {
            return null;
        }

        return {
            visible: true,
            title: this.translator.translate("interview_thread_completion_title"),
            titleId: this.completionModalTitleId,
            bodyHtml: this.getCompletionModalBodyHtml(),
            bodyId: this.completionModalBodyId,
            durationLabel: this.testMode
                ? this.translator.translate("interview_test_duration")
                : null,
            duration: this.testMode ? this.elapsedTime : null,
            buttonLabel: this.translator.translate("interview_thread_completion_leave"),
            imageUrl: "/assets/images/messages/finished-task.svg",
        };
    }

    private getCompletionModalBodyHtml(): string {
        const rewardPoints = this.completionModalOptions?.rewardPoints;
        if (rewardPoints !== null && rewardPoints !== undefined) {
            return this.translator
                .translate("interview_thread_completion_with_points")
                .replace("%points%", String(rewardPoints));
        }

        return this.translator.translate("interview_thread_completion_without_points");
    }

    public view(): Vnode {
        const className = this.testMode && this.goalsSidebarOpen
            ? ".interview-thread.row.g-0.h-100.interview-thread-goals-open"
            : ".interview-thread.row.g-0.h-100";

        return m(className, [
            m(this.threadMainView, {
                model: this.getMainViewModel(),
                callbacks: this.threadMainCallbacks,
                translate: (key: string) => this.translator.translate(key),
            }),
            m(this.goalsSidebarView, {
                model: this.getGoalsSidebarViewModel(),
                translate: (key: string) => this.translator.translate(key),
                onOpen: () => {
                    this.goalsSidebarOpen = true;
                },
                onClose: () => {
                    this.goalsSidebarOpen = false;
                },
            }),
        ]);
    }

}
